import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Medal, Sprout, ArrowLeft } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Ranking = () => {
  const navigate = useNavigate();
  const [rankings, setRankings] = useState({ all: [], month: [], week: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchRankings(); }, []);

  const fetchRankings = async () => {
    try {
      const [allRes, monthRes, weekRes] = await Promise.all([
        axios.get(`${API}/ranking?period=all`),
        axios.get(`${API}/ranking?period=month`),
        axios.get(`${API}/ranking?period=week`)
      ]);
      setRankings({ all: allRes.data, month: monthRes.data, week: weekRes.data });
    } catch (error) {
      console.error('Erro ao carregar ranking:', error);
    } finally { setLoading(false); }
  };

  const getMedalIcon = (posicao) => {
    if (posicao === 1) return <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-500 flex items-center justify-center shadow-lg"><Medal className="w-5 h-5 text-white" /></div>;
    if (posicao === 2) return <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center shadow-lg"><Medal className="w-5 h-5 text-white" /></div>;
    if (posicao === 3) return <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg"><Medal className="w-5 h-5 text-white" /></div>;
    return <span className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-500">{posicao}</span>;
  };

  const getPositionColor = (posicao) => {
    if (posicao === 1) return 'bg-yellow-50 border-yellow-300';
    if (posicao === 2) return 'bg-gray-100 border-gray-300';
    if (posicao === 3) return 'bg-orange-50 border-orange-300';
    return 'bg-white border-[#E1ECE5]';
  };

  const TopThree = ({ data, title, testIdPrefix, gender }) => {
    const items = data.slice(0, 3);
    if (items.length === 0) return null;
    const isBoy = gender === 'M';
    const accentColor = isBoy ? 'text-blue-700' : 'text-pink-600';
    const bgAccent = isBoy ? 'bg-blue-50 border-blue-200' : 'bg-pink-50 border-pink-200';
    return (
      <div className="mb-6">
        <h3 className={`font-nunito font-bold text-lg mb-3 ${accentColor}`}>{title}</h3>
        <div className="space-y-2">
          {items.map((item, index) => {
            const posicao = index + 1;
            return (
              <Card
                key={item.child_id}
                data-testid={`${testIdPrefix}-${posicao}`}
                onClick={() => navigate(`/participante/${item.child_id}`)}
                className={`rounded-xl p-4 shadow-sm border-2 hover:shadow-md transition-all cursor-pointer ${posicao <= 3 ? bgAccent : 'bg-white border-[#E1ECE5]'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getMedalIcon(posicao)}
                    <div>
                      <h4 className={`font-nunito font-bold text-lg ${accentColor}`}>{item.child_name}</h4>
                      <p className="font-inter text-xs text-gray-500">{posicao}o lugar</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Sprout className="w-5 h-5 text-[#F4D35E]" />
                    <p className={`font-nunito font-bold text-2xl ${accentColor}`}>{item.total_pontos}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  const RankingList = ({ data, testIdPrefix }) => {
    const meninos = data.filter((i) => i.sexo === 'M');
    const meninas = data.filter((i) => i.sexo === 'F');
    const restGeral = data.slice(3);

    return (
      <div className="space-y-2">
        {/* Top 3 separados */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <TopThree data={meninos} title="Top 3 Meninos" testIdPrefix={`${testIdPrefix}-meninos`} gender="M" />
          <TopThree data={meninas} title="Top 3 Meninas" testIdPrefix={`${testIdPrefix}-meninas`} gender="F" />
        </div>

        {/* Ranking Geral Completo */}
        {data.length > 0 && (
          <div>
            <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-3">Ranking Geral</h3>
            <div className="space-y-2">
              {data.map((item, index) => {
                const posicao = index + 1;
                return (
                  <Card
                    key={item.child_id}
                    data-testid={`${testIdPrefix}-${posicao}`}
                    onClick={() => navigate(`/participante/${item.child_id}`)}
                    className={`rounded-xl p-4 shadow-sm border-2 hover:shadow-md transition-all cursor-pointer ${getPositionColor(posicao)}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getMedalIcon(posicao)}
                        <div>
                          <h4 className="font-nunito font-bold text-lg text-[#1A4D2E]">{item.child_name}</h4>
                          <p className="font-inter text-xs text-gray-500">
                            {posicao}o lugar {item.sexo === 'M' ? '(M)' : '(F)'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1.5 justify-end">
                          <Sprout className="w-5 h-5 text-[#F4D35E]" />
                          <p className="font-nunito font-bold text-2xl text-[#1A4D2E]">{item.total_pontos}</p>
                        </div>
                        <p className="font-inter text-xs text-gray-500">sementes</p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {data.length === 0 && (
          <Card className="bg-white rounded-2xl p-8 text-center shadow-md border border-[#E1ECE5]">
            <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="font-inter text-gray-500">Nenhum dado de ranking ainda</p>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Trophy className="w-7 h-7" strokeWidth={2.5} />
              <h1 className="font-nunito font-bold text-3xl">Ranking</h1>
            </div>
            <img src="/logos/logo_semente.png" alt="Projeto Semente" className="w-36 h-36 drop-shadow-lg" />
          </div>
          <p className="font-inter text-white/70 text-sm">Acompanhe os destaques do projeto</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-12">
            <p className="font-inter text-gray-500">Carregando...</p>
          </div>
        ) : (
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="all" data-testid="tab-all">Geral</TabsTrigger>
              <TabsTrigger value="month" data-testid="tab-month">Mes</TabsTrigger>
              <TabsTrigger value="week" data-testid="tab-week">Semana</TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              <RankingList data={rankings.all} testIdPrefix="ranking-all" />
            </TabsContent>

            <TabsContent value="month">
              <RankingList data={rankings.month} testIdPrefix="ranking-month" />
            </TabsContent>

            <TabsContent value="week">
              <RankingList data={rankings.week} testIdPrefix="ranking-week" />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <Navigation active="ranking" />
    </div>
  );
};

export default Ranking;
